import axios from 'axios';
import { 
  API_BASE_URL, 
  getTokenFromStorage,
  STORAGE_KEYS
} from '../constant/constant';

// Create axios instance for content management
const createContentApi = () => {
  const baseURL = `${API_BASE_URL}/admin/content`;
  console.log('🌐 Creating content API with baseURL:', baseURL);
  
  const contentApi = axios.create({
    baseURL: baseURL,
  });

  // Request interceptor to add auth token
  contentApi.interceptors.request.use(
    (config) => {
      const token = getTokenFromStorage();
      console.log('🌐 Content API request:', config.url, 'Token:', token ? 'Present' : 'Missing');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor for error handling
  contentApi.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        // Token expired or invalid, clear Redux Persist storage and redirect to login
        localStorage.removeItem(STORAGE_KEYS.PERSIST_ROOT);
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );

  return contentApi;
};

const contentApi = createContentApi();

// Helper function to handle API errors
const handleApiError = (error, defaultMessage) => {
  const errorMessage = error.response?.data?.msg || error.message || defaultMessage;
  throw new Error(errorMessage);
};

// Helper function to build query parameters
const buildQueryParams = (params) => {
  const queryParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value.toString());
    }
  });
  
  return queryParams.toString();
};

// Content Management API Service Functions
export const getContentMetrics = async () => {
  try {
    console.log('🌐 API: Getting content metrics from /dashboard/metrics');
    const response = await contentApi.get('/dashboard/metrics');
    console.log('🌐 API: Content metrics response:', response.data);
    return response.data;
  } catch (error) {
    console.error('🌐 API: Content metrics error:', error);
    handleApiError(error, 'Failed to fetch content metrics');
  }
};

// Legacy function for backward compatibility
export const getBlogMetrics = getContentMetrics;

// 2. Get All Blog Posts
export const getBlogPosts = async (params = {}) => {
  try {
    const {
      page = 1,
      limit = 10,
      search = '',
      status = 'all',
      sortBy = 'createdAt',
      sortOrder = 'desc'
    } = params;

    const queryParams = buildQueryParams({
      page,
      limit,
      search,
      status,
      sortBy,
      sortOrder
    });

    console.log('🌐 API: Getting blog posts with params:', queryParams);
    const response = await contentApi.get(`/blogs?${queryParams}`);
    console.log('🌐 API: Blog posts response:', response.data);
    return response.data;
  } catch (error) {
    console.error('🌐 API: Blog posts error:', error);
    handleApiError(error, 'Failed to fetch blog posts');
  }
};

// 3. Get Single Blog Post
export const getBlogPost = async (blogId) => {
  try {
    const response = await contentApi.get(`/blogs/${blogId}`);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch blog post');
  }
};

// 4. Create Blog Post
export const createBlogPost = async (blogData) => {
  try {
    const response = await contentApi.post('/blogs', blogData);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to create blog post');
  }
};

// 5. Update Blog Post
export const updateBlogPost = async (blogId, blogData) => {
  try {
    const response = await contentApi.put(`/blogs/${blogId}`, blogData);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to update blog post');
  }
};

// 6. Delete Blog Post
export const deleteBlogPost = async (blogId) => {
  try {
    const response = await contentApi.delete(`/blogs/${blogId}`);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to delete blog post');
  }
};

// 7. Update Blog Status
export const updateBlogStatus = async (blogId, status) => {
  try {
    const response = await contentApi.patch(`/blogs/${blogId}/status`, { status });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to update blog status');
  }
};

// 8. Search Blog Posts
export const searchBlogPosts = async (query, limit = 10) => {
  try {
    const response = await contentApi.get(`/blogs/search?q=${encodeURIComponent(query)}&limit=${limit}`);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to search blog posts');
  }
};

// 9. Get Blog Analytics
export const getBlogAnalytics = async (timeframe = '30d') => {
  try {
    const response = await contentApi.get(`/blogs/analytics?timeframe=${timeframe}`);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch blog analytics');
  }
};

// 10. Bulk Operations
export const bulkActionBlogs = async (action, blogIds) => {
  try {
    const response = await contentApi.post('/blogs/bulk-action', {
      action,
      blogIds,
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to perform bulk action');
  }
};

// FAQ Management API Functions
export const getFAQs = async (params = {}) => {
  try {
    const {
      page = 1,
      limit = 10,
      search = '',
      status = 'all',
      category = 'all',
      sortBy = 'priority',
      sortOrder = 'desc'
    } = params;

    const queryParams = buildQueryParams({
      page,
      limit,
      search,
      status,
      category,
      sortBy,
      sortOrder
    });

    console.log('🌐 API: Getting FAQs with params:', queryParams);
    const response = await contentApi.get(`/faqs?${queryParams}`);
    console.log('🌐 API: FAQs response:', response.data);
    return response.data;
  } catch (error) {
    console.error('🌐 API: FAQs error:', error);
    handleApiError(error, 'Failed to fetch FAQs');
  }
};

export const getFAQ = async (faqId) => {
  try {
    const response = await contentApi.get(`/faqs/${faqId}`);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch FAQ');
  }
};

export const createFAQ = async (faqData) => {
  try {
    const response = await contentApi.post('/faqs', faqData);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to create FAQ');
  }
};

export const updateFAQ = async (faqId, faqData) => {
  try {
    const response = await contentApi.put(`/faqs/${faqId}`, faqData);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to update FAQ');
  }
};

export const deleteFAQ = async (faqId) => {
  try {
    const response = await contentApi.delete(`/faqs/${faqId}`);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to delete FAQ');
  }
};

export const updateFAQStatus = async (faqId, status) => {
  try {
    const response = await contentApi.patch(`/faqs/${faqId}/status`, { status });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to update FAQ status');
  }
};

export const updateFAQPriority = async (faqId, priority) => {
  try {
    const response = await contentApi.patch(`/faqs/${faqId}/priority`, { priority });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to update FAQ priority');
  }
};

export const searchFAQs = async (query, limit = 10, category = 'all') => {
  try {
    const response = await contentApi.get(`/faqs/search?q=${encodeURIComponent(query)}&limit=${limit}&category=${category}`);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to search FAQs');
  }
};

export const getFAQCategories = async () => {
  try {
    const response = await contentApi.get('/faqs/categories');
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch FAQ categories');
  }
};

export const getFAQAnalytics = async (timeframe = '30d') => {
  try {
    const response = await contentApi.get(`/faqs/analytics?timeframe=${timeframe}`);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch FAQ analytics');
  }
};

export const bulkActionFAQs = async (action, faqIds) => {
  try {
    const response = await contentApi.post('/faqs/bulk-action', {
      action,
      faqIds,
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to perform bulk action on FAQs');
  }
};

// Export all functions as contentManagementService object for backward compatibility
export const contentManagementService = {
  // Content metrics
  getContentMetrics,
  getBlogMetrics,
  
  // Blog functions
  getBlogPosts,
  getBlogPost,
  createBlogPost,
  updateBlogPost,
  deleteBlogPost,
  updateBlogStatus,
  searchBlogPosts,
  getBlogAnalytics,
  bulkActionBlogs,
  
  // FAQ functions
  getFAQs,
  getFAQ,
  createFAQ,
  updateFAQ,
  deleteFAQ,
  updateFAQStatus,
  updateFAQPriority,
  searchFAQs,
  getFAQCategories,
  getFAQAnalytics,
  bulkActionFAQs
};

// Helper function to format blog data for display
export const formatBlogData = (blog) => {
  return {
    id: blog._id,
    no: blog.no,
    title: blog.title,
    author: blog.author,
    status: {
      name: blog.status.name,
      color: blog.status.color,
    },
    views: blog.views,
    publishDate: blog.publishDate,
    createdAt: blog.createdAt,
    lastModified: blog.lastModified,
    slug: blog.slug,
    category: blog.category,
    tags: blog.tags,
  };
};

// Helper function to format FAQ data for display
export const formatFAQData = (faq) => {
  return {
    id: faq._id,
    no: faq.no,
    question: faq.question,
    answer: faq.answer,
    category: faq.category,
    subcategory: faq.subcategory,
    author: faq.author,
    status: {
      name: faq.status.name,
      color: faq.status.color,
    },
    priority: faq.priority,
    views: faq.views,
    helpful: faq.helpful,
    notHelpful: faq.notHelpful,
    helpfulPercentage: faq.helpfulPercentage,
    publishDate: faq.publishDate,
    createdAt: faq.createdAt,
    lastModified: faq.lastModified,
    tags: faq.tags,
  };
};

// Helper function to format metrics data
export const formatMetricsData = (metrics, contentType = 'blogs') => {
  if (contentType === 'faqs' && metrics.faqs) {
    return {
      totalFAQs: {
        value: metrics.faqs.totalFAQs.value,
        icon: metrics.faqs.totalFAQs.icon,
        color: metrics.faqs.totalFAQs.color,
      },
      published: {
        value: metrics.faqs.published.value,
        icon: metrics.faqs.published.icon,
        color: metrics.faqs.published.color,
      },
      drafts: {
        value: metrics.faqs.drafts.value,
        icon: metrics.faqs.drafts.icon,
        color: metrics.faqs.drafts.color,
      },
      archived: {
        value: metrics.faqs.archived.value,
        icon: metrics.faqs.archived.icon,
        color: metrics.faqs.archived.color,
      },
      totalViews: {
        value: metrics.faqs.totalViews.value,
        icon: metrics.faqs.totalViews.icon,
        color: metrics.faqs.totalViews.color,
      },
    };
  }
  
  // Default to blogs format
  const blogMetrics = metrics.blogs || metrics;
  return {
    totalArticles: {
      value: blogMetrics.totalArticles?.value || 0,
      icon: blogMetrics.totalArticles?.icon || 'documents',
      color: blogMetrics.totalArticles?.color || 'blue',
    },
    published: {
      value: blogMetrics.published?.value || 0,
      icon: blogMetrics.published?.icon || 'check-circle',
      color: blogMetrics.published?.color || 'green',
    },
    drafts: {
      value: blogMetrics.drafts?.value || 0,
      icon: blogMetrics.drafts?.icon || 'pencil',
      color: blogMetrics.drafts?.color || 'blue',
    },
    archived: {
      value: blogMetrics.archived?.value || 0,
      icon: blogMetrics.archived?.icon || 'archive',
      color: blogMetrics.archived?.color || 'purple',
    },
    totalViews: {
      value: blogMetrics.totalViews?.value || 0,
      icon: blogMetrics.totalViews?.icon || 'eye',
      color: blogMetrics.totalViews?.color || 'gray',
    },
  };
};

// Status color mapping
export const getStatusColor = (status) => {
  const statusColors = {
    published: 'bg-green-100 text-green-800',
    draft: 'bg-orange-100 text-orange-800',
    archived: 'bg-purple-100 text-purple-800',
  };
  return statusColors[status] || 'bg-gray-100 text-gray-800';
};

// Icon mapping for metrics
export const getMetricIcon = (iconName) => {
  const iconMap = {
    documents: 'FileText',
    'check-circle': 'CheckCircle',
    pencil: 'Edit3',
    archive: 'Archive',
    eye: 'Eye',
  };
  return iconMap[iconName] || 'FileText';
};

// Color mapping for metrics
export const getMetricColor = (colorName) => {
  const colorMap = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    gray: 'bg-gray-100 text-gray-600',
  };
  return colorMap[colorName] || 'bg-gray-100 text-gray-600';
};

export default contentManagementService;
